import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Save } from "lucide-react";
import {
  BUSINESS_LINES,
  BUSINESS_LINE_LABELS,
  type BusinessLine,
  type RevenueGoal,
  type RevenueGoalInput,
} from "@monitoring-panel/types";
import { useRevenueGoals, useUpdateRevenueGoals } from "@/hooks/useRevenue";
import { formatNumber } from "@/lib/utils";

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

function goalKey(businessLine: BusinessLine, month: string): string {
  return `${businessLine}|${month}`;
}

function monthsOfYear(year: number): string[] {
  return MONTH_LABELS.map((_, index) => `${year}-${String(index + 1).padStart(2, "0")}`);
}

/**
 * Monthly revenue goals per business line. Only the revenue admins reach this
 * page (the tab is hidden for everyone else and the API refuses their saves).
 */
export function RevenueGoalsView() {
  const { data, isLoading, error } = useRevenueGoals();
  const updateGoals = useUpdateRevenueGoals();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  // Edited cells only, as typed; everything else shows the stored goal.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const storedGoals = useMemo(() => {
    const goals = new Map<string, RevenueGoal>();
    for (const goal of data?.goals ?? []) goals.set(goalKey(goal.businessLine, goal.month), goal);
    return goals;
  }, [data?.goals]);

  const months = monthsOfYear(year);

  const cellAmount = (businessLine: BusinessLine, month: string): number => {
    const key = goalKey(businessLine, month);
    if (key in drafts) return Number(drafts[key]) || 0;
    return storedGoals.get(key)?.amount ?? 0;
  };

  const changedGoals: RevenueGoalInput[] = Object.entries(drafts)
    .map(([key, value]) => {
      const [businessLine, month] = key.split("|") as [BusinessLine, string];
      return { businessLine, month, amount: Number(value) || 0 };
    })
    .filter((goal) => goal.amount !== (storedGoals.get(goalKey(goal.businessLine, goal.month))?.amount ?? 0));

  const handleSave = () => {
    updateGoals.mutate(
      { goals: changedGoals },
      {
        onSuccess: () => {
          setDrafts({});
          toast.success("Goals saved");
        },
        onError: (saveError) => toast.error((saveError as Error).message),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-adv-tertiary" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="adv-alert">
        <p>Error: {(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.canEdit) {
    return (
      <div className="adv-card adv-state">
        <p className="adv-state__desc">Only the revenue admins can set the goals.</p>
      </div>
    );
  }

  const yearTotal = (businessLine: BusinessLine): number =>
    months.reduce((total, month) => total + cellAmount(businessLine, month), 0);

  return (
    <div className="adv-card adv-card--featured overflow-hidden">
      <div className="px-6 py-4 border-b border-adv-border flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="adv-section-title">Revenue Goals</h2>
          <p className="adv-section-desc mt-0.5">
            Monthly goal per business line, spread evenly over the days of the month
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="adv-input adv-num"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button
            onClick={handleSave}
            disabled={changedGoals.length === 0 || updateGoals.isPending}
            className="adv-btn adv-btn--primary"
          >
            {updateGoals.isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save{changedGoals.length > 0 ? ` (${changedGoals.length})` : ""}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="adv-table">
          <thead className="bg-adv-sunken">
            <tr>
              <th className="px-4 py-3 text-left">Month</th>
              {BUSINESS_LINES.map((businessLine) => (
                <th key={businessLine} className="px-4 py-3 text-right">
                  {BUSINESS_LINE_LABELS[businessLine]}
                </th>
              ))}
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month, index) => (
              <tr key={month}>
                <td className="px-4 py-2 whitespace-nowrap">{MONTH_LABELS[index]}</td>
                {BUSINESS_LINES.map((businessLine) => {
                  const key = goalKey(businessLine, month);
                  const stored = storedGoals.get(key);
                  return (
                    <td key={businessLine} className="px-4 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        step={100}
                        value={key in drafts ? drafts[key] : stored?.amount ?? ""}
                        placeholder="0"
                        onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                        title={
                          stored?.updatedBy
                            ? `Last set by ${stored.updatedBy} on ${new Date(stored.updatedAt).toLocaleDateString("es-ES")}`
                            : undefined
                        }
                        className={`adv-input adv-input--sm adv-num w-36 text-right ${
                          key in drafts ? "adv-input--edited" : ""
                        }`}
                      />
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right adv-num font-bold whitespace-nowrap">
                  {formatNumber(
                    BUSINESS_LINES.reduce((total, businessLine) => total + cellAmount(businessLine, month), 0),
                    "euro"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold">
              <td className="px-4 py-3">
                Total <span className="adv-num">{year}</span>
              </td>
              {BUSINESS_LINES.map((businessLine) => (
                <td key={businessLine} className="px-4 py-3 text-right adv-num whitespace-nowrap">
                  {formatNumber(yearTotal(businessLine), "euro")}
                </td>
              ))}
              <td className="px-4 py-3 text-right adv-num whitespace-nowrap">
                {formatNumber(
                  BUSINESS_LINES.reduce((total, businessLine) => total + yearTotal(businessLine), 0),
                  "euro"
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
